import { useState, useEffect } from 'react';
import { Sparkles, Database, FileText, RefreshCw, CheckCircle2, AlertCircle, AlertTriangle, XCircle, Pencil, Check, X, Plus, Trash2, History, Target, GitBranch, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import AnalysisHistory from '../project/AnalysisHistory';
import CompletenessGapManager from '../project/CompletenessGapManager';

interface DataRequirementsFormProps {
  caseId?: string;
  onComplete?: () => void;
}

interface DataRequirement {
  category: 'master_data' | 'transactional_data';
  entity: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  attributes: string[];
  reasoning: string;
}

interface DataRelationship {
  relationshipId?: string;
  relationshipType: string;
  fromEntity: string;
  toEntity: string;
  description: string;
  direction: 'outbound' | 'inbound' | 'bidirectional';
  cardinality: 'one-to-one' | 'one-to-many' | 'many-to-one' | 'many-to-many';
  properties: string[];
  createdAt?: string;
  createdBy?: string;
}

interface DataRequirementsAnalysis {
  masterData: DataRequirement[];
  transactionalData: DataRequirement[];
  relationships?: DataRelationship[];
  summary: string;
  recommendations: string[];
  completenessScore?: number;
  completenessGaps?: string[];
  missingDataPoints?: string[];
  clarificationNeeded?: string[];
  validationWarnings?: string[];
  validationErrors?: string[];
}

export default function DataRequirementsForm({ caseId, onComplete }: DataRequirementsFormProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DataRequirementsAnalysis | null>(null);
  const [hasRun, setHasRun] = useState(false);

  // Edit state
  const [editingItem, setEditingItem] = useState<{ category: 'master' | 'transactional', index: number } | null>(null);
  const [editForm, setEditForm] = useState<DataRequirement>({
    category: 'master_data',
    entity: '',
    description: '',
    priority: 'medium',
    attributes: [],
    reasoning: ''
  });
  const [newAttribute, setNewAttribute] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Add new requirement state
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRequirementCategory, setNewRequirementCategory] = useState<'master_data' | 'transactional_data'>('master_data');

  // History modal state
  const [showHistory, setShowHistory] = useState(false);

  // Load existing data requirements or run analysis when component mounts
  useEffect(() => {
    if (caseId && !hasRun) {
      loadExistingAnalysis();
    }
  }, [caseId]);

  const loadExistingAnalysis = async () => {
    if (!caseId) return;

    try {
      const response = await api.get(`/data-requirements/cases/${caseId}/data-requirements`);

      // Check if there's existing data
      if (response.masterData && response.masterData.length > 0 ||
          response.transactionalData && response.transactionalData.length > 0) {
        setAnalysis(response);
        setHasRun(true);
      } else {
        // No existing data, run analysis
        handleAnalyze();
      }
    } catch (error: any) {
      // If 404 or error, run fresh analysis
      console.log('No existing data requirements found, running analysis...');
      handleAnalyze();
    }
  };

  const handleAnalyze = async () => {
    if (!caseId) {
      toast.error('Project ID is required');
      return;
    }

    setIsAnalyzing(true);
    setHasRun(true);

    try {
      toast.loading('Analyzing data requirements...', { id: 'data-analysis' });

      const response = await api.post('/data-requirements/analyze', {
        caseId
      });

      setAnalysis(response);

      // Save analysis to Neo4j
      try {
        await api.post(`/data-requirements/cases/${caseId}/data-requirements`, {
          caseId,
          ...response
        });
        toast.success('Data requirements analyzed and saved!', { id: 'data-analysis' });
      } catch (saveError: any) {
        console.error('Error saving data requirements:', saveError);
        toast.warning('Analysis complete but failed to save to database', { id: 'data-analysis' });
      }

      if (onComplete) {
        onComplete();
      }
    } catch (error: any) {
      console.error('Data requirements analysis error:', error);
      toast.error(
        error?.response?.data?.detail || 'Failed to analyze data requirements',
        { id: 'data-analysis' }
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'low':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-neutral-600 bg-neutral-50 border-neutral-200';
    }
  };

  const handleStartEdit = (category: 'master' | 'transactional', index: number) => {
    const requirement = category === 'master' ? analysis!.masterData[index] : analysis!.transactionalData[index];
    setEditingItem({ category, index });
    setEditForm({ ...requirement });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditForm({
      category: 'master_data',
      entity: '',
      description: '',
      priority: 'medium',
      attributes: [],
      reasoning: ''
    });
    setNewAttribute('');
  };

  const handleAddAttribute = () => {
    if (newAttribute.trim()) {
      setEditForm({
        ...editForm,
        attributes: [...editForm.attributes, newAttribute.trim()]
      });
      setNewAttribute('');
    }
  };

  const handleRemoveAttribute = (index: number) => {
    setEditForm({
      ...editForm,
      attributes: editForm.attributes.filter((_, i) => i !== index)
    });
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !analysis || !caseId) return;

    setIsSaving(true);
    try {
      // Update local state
      const updatedAnalysis = { ...analysis };
      if (editingItem.category === 'master') {
        updatedAnalysis.masterData[editingItem.index] = editForm;
      } else {
        updatedAnalysis.transactionalData[editingItem.index] = editForm;
      }

      // Save to backend
      await api.post(`/data-requirements/cases/${caseId}/data-requirements`, {
        caseId,
        ...updatedAnalysis
      });

      setAnalysis(updatedAnalysis);
      toast.success('Data requirement updated successfully!');
      handleCancelEdit();
    } catch (error: any) {
      console.error('Error updating data requirement:', error);
      toast.error('Failed to update data requirement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartAddNew = (category: 'master_data' | 'transactional_data') => {
    setIsAddingNew(true);
    setNewRequirementCategory(category);
    setEditForm({
      category,
      entity: '',
      description: '',
      priority: 'medium',
      attributes: [],
      reasoning: ''
    });
  };

  const handleCancelAddNew = () => {
    setIsAddingNew(false);
    setEditForm({
      category: 'master_data',
      entity: '',
      description: '',
      priority: 'medium',
      attributes: [],
      reasoning: ''
    });
    setNewAttribute('');
  };

  const handleSaveNew = async () => {
    if (!analysis || !caseId) return;

    setIsSaving(true);
    try {
      // Add to local state
      const updatedAnalysis = { ...analysis };
      if (newRequirementCategory === 'master_data') {
        updatedAnalysis.masterData = [...updatedAnalysis.masterData, { ...editForm, category: 'master_data' }];
      } else {
        updatedAnalysis.transactionalData = [...updatedAnalysis.transactionalData, { ...editForm, category: 'transactional_data' }];
      }

      // Save to backend
      await api.post(`/data-requirements/cases/${caseId}/data-requirements`, {
        caseId,
        ...updatedAnalysis
      });

      setAnalysis(updatedAnalysis);
      toast.success('Data requirement added successfully!');
      handleCancelAddNew();
    } catch (error: any) {
      console.error('Error adding data requirement:', error);
      toast.error('Failed to add data requirement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequirement = async (category: 'master' | 'transactional', index: number) => {
    if (!analysis || !caseId) return;

    const requirement = category === 'master' ? analysis.masterData[index] : analysis.transactionalData[index];

    if (!confirm(`Are you sure you want to delete "${requirement.entity}"?`)) {
      return;
    }

    try {
      // Update local state
      const updatedAnalysis = { ...analysis };
      if (category === 'master') {
        updatedAnalysis.masterData = updatedAnalysis.masterData.filter((_, i) => i !== index);
      } else {
        updatedAnalysis.transactionalData = updatedAnalysis.transactionalData.filter((_, i) => i !== index);
      }

      // Save to backend
      await api.post(`/data-requirements/cases/${caseId}/data-requirements`, {
        caseId,
        ...updatedAnalysis
      });

      setAnalysis(updatedAnalysis);
      toast.success('Data requirement deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting data requirement:', error);
      toast.error('Failed to delete data requirement');
    }
  };

  const handleViewAnalysis = async (analysisId: string) => {
    if (!caseId) return;

    try {
      toast.loading('Loading analysis...', { id: 'load-analysis' });

      const response = await api.get(`/data-requirements/cases/${caseId}/data-requirements/${analysisId}`);

      setAnalysis(response);
      toast.success('Analysis loaded successfully', { id: 'load-analysis' });
    } catch (error: any) {
      console.error('Error loading analysis:', error);
      toast.error('Failed to load analysis', { id: 'load-analysis' });
    }
  };

  if (isAnalyzing && !analysis) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Spinner size="lg" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Analyzing Data Requirements
          </h3>
          <p className="text-neutral-600 max-w-md">
            Business Transformation Studio is analyzing your business domain, executive needs, and client details to determine what master data and transactional data will be required...
          </p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-primary-600" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-neutral-900 mb-3">
            Data Requirements Analysis
          </h3>
          <p className="text-neutral-600 max-w-2xl mx-auto mb-6">
            Based on your business domain, executive needs, and client details, Business Transformation Studio will analyze and determine:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-8">
            <Card className="p-4 text-left">
              <div className="flex items-start gap-3">
                <Database className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Master Data</h4>
                  <p className="text-sm text-neutral-600">
                    Core entities like customers, products, suppliers, and organizational structures
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-4 text-left">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-secondary-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-neutral-900 mb-1">Transactional Data</h4>
                  <p className="text-sm text-neutral-600">
                    Business events like orders, payments, shipments, and interactions
                  </p>
                </div>
              </div>
            </Card>
          </div>
          <Button
            onClick={handleAnalyze}
            size="lg"
            disabled={!caseId}
          >
            <Sparkles className="w-5 h-5" />
            Start Analysis
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Section */}
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-6 rounded-lg border border-primary-100">
        <div className="flex items-start gap-3 mb-4">
          <CheckCircle2 className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-neutral-900 mb-2">Analysis Complete</h3>
            <p className="text-neutral-700">{analysis.summary}</p>
          </div>
        </div>

        {analysis.completenessScore !== undefined && (
          <div className="mt-4 pt-4 border-t border-primary-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-neutral-700">
                Completeness Score
              </span>
              <span className="text-2xl font-bold text-primary-600">
                {analysis.completenessScore}%
              </span>
            </div>
            <div className="mt-2 w-full bg-neutral-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{ width: `${analysis.completenessScore}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Validation Warnings and Errors */}
      {((analysis.validationErrors && analysis.validationErrors.length > 0) ||
        (analysis.validationWarnings && analysis.validationWarnings.length > 0)) && (
        <div className="space-y-3">
          {/* Critical Errors */}
          {analysis.validationErrors && analysis.validationErrors.length > 0 && (
            <div className="bg-red-50 border-l-4 border-l-red-500 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-2">
                    Critical Validation Errors
                  </h4>
                  <ul className="space-y-1">
                    {analysis.validationErrors.map((error, index) => (
                      <li key={index} className="text-sm text-red-800">
                        {error}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-red-700 mt-2 italic">
                    Please review and fix these critical issues before proceeding.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Warnings */}
          {analysis.validationWarnings && analysis.validationWarnings.length > 0 && (
            <div className="bg-amber-50 border-l-4 border-l-amber-500 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900 mb-2">
                    Validation Warnings
                  </h4>
                  <ul className="space-y-1">
                    {analysis.validationWarnings.map((warning, index) => (
                      <li key={index} className="text-sm text-amber-800">
                        {warning}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-700 mt-2 italic">
                    These are potential issues that may need your attention.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completeness Gap Management - Interactive UI for individual gap resolution */}
      {analysis.completenessScore !== undefined && analysis.completenessScore < 100 && caseId && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-bold text-neutral-900">
              Completeness Gap Resolution
            </h3>
          </div>
          <CompletenessGapManager caseId={caseId} />
        </div>
      )}

      {/* Master Data Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-bold text-neutral-900">
              Master Data Requirements
            </h3>
            <span className="text-sm text-neutral-500">
              ({analysis.masterData.length} entities)
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleStartAddNew('master_data')}
            disabled={isAddingNew}
          >
            <Plus className="w-4 h-4" />
            Add Master Data
          </Button>
        </div>

        <div className="space-y-3">
          {analysis.masterData.map((req, index) => (
            <Card key={index} className="p-4 border-l-4 border-l-primary-500">
              {editingItem?.category === 'master' && editingItem.index === index ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Entity Name"
                      value={editForm.entity}
                      onChange={(e) => setEditForm({ ...editForm, entity: e.target.value })}
                      placeholder="e.g., Product Master"
                    />
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={editForm.priority}
                        onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as 'high' | 'medium' | 'low' })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>

                  <Textarea
                    label="Description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    placeholder="Describe what this data represents and why it's needed..."
                  />

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Key Attributes
                    </label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={newAttribute}
                        onChange={(e) => setNewAttribute(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
                        placeholder="Add attribute (e.g., SKU, Name, Price)"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddAttribute}
                        disabled={!newAttribute.trim()}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {editForm.attributes.map((attr, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-700 rounded flex items-center gap-1"
                        >
                          {attr}
                          <button
                            onClick={() => handleRemoveAttribute(idx)}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <Textarea
                    label="Reasoning"
                    value={editForm.reasoning}
                    onChange={(e) => setEditForm({ ...editForm, reasoning: e.target.value })}
                    rows={2}
                    placeholder="Why is this data requirement needed for the project?"
                  />

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={!editForm.entity.trim() || !editForm.description.trim() || isSaving}
                      loading={isSaving}
                    >
                      <Check className="w-4 h-4" />
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-neutral-900">{req.entity}</h4>
                    <div className="flex items-center gap-2">
                      {req.priority && (
                        <span
                          className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(
                            req.priority
                          )}`}
                        >
                          {req.priority.toUpperCase()}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit('master', index)}
                      >
                        <Pencil className="w-4 h-4 text-primary-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRequirement('master', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3">{req.description}</p>

                  <div className="mb-3">
                    <p className="text-xs font-medium text-neutral-500 mb-1">Key Attributes:</p>
                    <div className="flex flex-wrap gap-1">
                      {req.attributes.map((attr, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded"
                        >
                          {attr}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-neutral-100">
                    <p className="text-xs text-neutral-500 italic">{req.reasoning}</p>
                  </div>
                </div>
              )}
            </Card>
          ))}

          {/* Add New Master Data Form */}
          {isAddingNew && newRequirementCategory === 'master_data' && (
            <Card className="p-4 border-l-4 border-l-green-500 bg-green-50">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-neutral-900">Add New Master Data Requirement</h4>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Entity Name"
                    value={editForm.entity}
                    onChange={(e) => setEditForm({ ...editForm, entity: e.target.value })}
                    placeholder="e.g., Product Master, Customer Master"
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as 'high' | 'medium' | 'low' })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                <Textarea
                  label="Description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  placeholder="Describe what this data represents and why it's needed..."
                />

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Key Attributes
                  </label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newAttribute}
                      onChange={(e) => setNewAttribute(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
                      placeholder="Add attribute (e.g., SKU, Name, Category)"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddAttribute}
                      disabled={!newAttribute.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {editForm.attributes.map((attr, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-white text-neutral-700 rounded flex items-center gap-1 border border-neutral-300"
                      >
                        {attr}
                        <button
                          onClick={() => handleRemoveAttribute(idx)}
                          className="hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <Textarea
                  label="Reasoning"
                  value={editForm.reasoning}
                  onChange={(e) => setEditForm({ ...editForm, reasoning: e.target.value })}
                  rows={2}
                  placeholder="Why is this data requirement needed for the project?"
                />

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNew}
                    disabled={!editForm.entity.trim() || !editForm.description.trim() || isSaving}
                    loading={isSaving}
                  >
                    <Check className="w-4 h-4" />
                    Add Requirement
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelAddNew}
                    disabled={isSaving}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Transactional Data Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-secondary-600" />
            <h3 className="text-lg font-bold text-neutral-900">
              Transactional Data Requirements
            </h3>
            <span className="text-sm text-neutral-500">
              ({analysis.transactionalData.length} entities)
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleStartAddNew('transactional_data')}
            disabled={isAddingNew}
          >
            <Plus className="w-4 h-4" />
            Add Transactional Data
          </Button>
        </div>

        <div className="space-y-3">
          {analysis.transactionalData.map((req, index) => (
            <Card key={index} className="p-4 border-l-4 border-l-secondary-500">
              {editingItem?.category === 'transactional' && editingItem.index === index ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Entity Name"
                      value={editForm.entity}
                      onChange={(e) => setEditForm({ ...editForm, entity: e.target.value })}
                      placeholder="e.g., Sales Order"
                    />
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={editForm.priority}
                        onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as 'high' | 'medium' | 'low' })}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>

                  <Textarea
                    label="Description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    placeholder="Describe what this data represents and why it's needed..."
                  />

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Key Attributes
                    </label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={newAttribute}
                        onChange={(e) => setNewAttribute(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
                        placeholder="Add attribute (e.g., Order ID, Date, Amount)"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddAttribute}
                        disabled={!newAttribute.trim()}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {editForm.attributes.map((attr, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 bg-neutral-100 text-neutral-700 rounded flex items-center gap-1"
                        >
                          {attr}
                          <button
                            onClick={() => handleRemoveAttribute(idx)}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <Textarea
                    label="Reasoning"
                    value={editForm.reasoning}
                    onChange={(e) => setEditForm({ ...editForm, reasoning: e.target.value })}
                    rows={2}
                    placeholder="Why is this data requirement needed for the project?"
                  />

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={!editForm.entity.trim() || !editForm.description.trim() || isSaving}
                      loading={isSaving}
                    >
                      <Check className="w-4 h-4" />
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-neutral-900">{req.entity}</h4>
                    <div className="flex items-center gap-2">
                      {req.priority && (
                        <span
                          className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(
                            req.priority
                          )}`}
                        >
                          {req.priority.toUpperCase()}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit('transactional', index)}
                      >
                        <Pencil className="w-4 h-4 text-secondary-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRequirement('transactional', index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3">{req.description}</p>

                  <div className="mb-3">
                    <p className="text-xs font-medium text-neutral-500 mb-1">Key Attributes:</p>
                    <div className="flex flex-wrap gap-1">
                      {req.attributes.map((attr, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded"
                        >
                          {attr}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-neutral-100">
                    <p className="text-xs text-neutral-500 italic">{req.reasoning}</p>
                  </div>
                </div>
              )}
            </Card>
          ))}

          {/* Add New Transactional Data Form */}
          {isAddingNew && newRequirementCategory === 'transactional_data' && (
            <Card className="p-4 border-l-4 border-l-green-500 bg-green-50">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-neutral-900">Add New Transactional Data Requirement</h4>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Entity Name"
                    value={editForm.entity}
                    onChange={(e) => setEditForm({ ...editForm, entity: e.target.value })}
                    placeholder="e.g., Sales Order, Purchase Order"
                  />
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as 'high' | 'medium' | 'low' })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                </div>

                <Textarea
                  label="Description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  placeholder="Describe what this data represents and why it's needed..."
                />

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Key Attributes
                  </label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newAttribute}
                      onChange={(e) => setNewAttribute(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
                      placeholder="Add attribute (e.g., Order ID, Date, Amount)"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddAttribute}
                      disabled={!newAttribute.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {editForm.attributes.map((attr, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 bg-white text-neutral-700 rounded flex items-center gap-1 border border-neutral-300"
                      >
                        {attr}
                        <button
                          onClick={() => handleRemoveAttribute(idx)}
                          className="hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <Textarea
                  label="Reasoning"
                  value={editForm.reasoning}
                  onChange={(e) => setEditForm({ ...editForm, reasoning: e.target.value })}
                  rows={2}
                  placeholder="Why is this data requirement needed for the project?"
                />

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNew}
                    disabled={!editForm.entity.trim() || !editForm.description.trim() || isSaving}
                    loading={isSaving}
                  >
                    <Check className="w-4 h-4" />
                    Add Requirement
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelAddNew}
                    disabled={isSaving}
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Data Relationships Section */}
      {analysis.relationships && analysis.relationships.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-bold text-neutral-900">
                Data Relationships
              </h3>
              <span className="text-sm text-neutral-500">
                ({analysis.relationships.length} relationships)
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {analysis.relationships.map((rel, index) => (
              <Card key={rel.relationshipId || index} className="p-4 border-l-4 border-l-purple-500">
                <div className="space-y-3">
                  {/* Relationship Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-purple-700 bg-purple-50 px-2 py-1 rounded">
                        {rel.relationshipType}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-700">
                        {rel.cardinality}
                      </span>
                    </div>
                    <span className="text-xs text-neutral-500 capitalize">
                      {rel.direction}
                    </span>
                  </div>

                  {/* From → To */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded">
                      {rel.fromEntity}
                    </span>
                    <ArrowRight className="w-4 h-4 text-neutral-400" />
                    <span className="font-semibold text-secondary-600 bg-secondary-50 px-3 py-1 rounded">
                      {rel.toEntity}
                    </span>
                  </div>

                  {/* Description */}
                  {rel.description && (
                    <p className="text-sm text-neutral-600 pl-2 border-l-2 border-neutral-200">
                      {rel.description}
                    </p>
                  )}

                  {/* Properties */}
                  {rel.properties && rel.properties.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-neutral-500 mb-1">Properties:</p>
                      <div className="flex flex-wrap gap-1">
                        {rel.properties.map((prop, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200"
                          >
                            {prop}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations Section */}
      {analysis.recommendations.length > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-neutral-900 mb-2">Recommendations</h4>
              <ul className="space-y-1">
                {analysis.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-neutral-700">
                    • {rec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Re-analyze Button */}
      <div className="flex justify-center gap-3 pt-4">
        <Button
          variant="outline"
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          loading={isAnalyzing}
        >
          <RefreshCw className="w-4 h-4" />
          Re-analyze Requirements
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowHistory(true)}
        >
          <History className="w-4 h-4" />
          View History
        </Button>
      </div>

      {/* History Modal */}
      {showHistory && caseId && (
        <AnalysisHistory
          caseId={caseId}
          onViewAnalysis={handleViewAnalysis}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
